/**
 * The import queue: scans waiting to become controlled documents.
 *
 * WHY THIS PAGE EXISTS AT ALL. A document import is NOT a species -- it is a child table, by the
 * programme's §2.1 test -- so it gets none of the generic list and detail pages every species has.
 * Without a screen of its own the whole capability is reachable only over HTTP, and the human
 * confirmation that promotion depends on is unreachable by the human it depends on.
 *
 * THREE ANSWERS IN THE HEADER, NOT TWO, AND THE MIDDLE ONE IS THE POINT. "Waiting for a person" is
 * not the same as "unread": those imports HAVE text and a confidence, frequently 1.00, and they
 * look finished on any list that counts read-versus-unread. They are the ones that cannot
 * legitimately be promoted, so they get a count of their own and the sentence that explains it.
 *
 * AND THE COST IS ON THE PAGE. Reading is billed per page at a third party, and an AWS bill cannot
 * be attributed to a tenant -- so the only place anybody can see what OCR is costing them is here.
 * A capability that spends money silently is one somebody discovers through an invoice.
 */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router";
import {Box, Chip, CircularProgress, Tooltip, Typography} from "@mui/material";

import {AppContext} from "../util/AppContext";
import {makeCresAxios} from "../util/Axios";
import {Grey2, Grey5, Grey7, Grey8, Grey17} from "../theme/styles";

type Import_ = {
    USID: string; Origin?: string | null; OriginDetail?: string | null;
    ImportedDatetime?: string | null; Status?: string | null;
    DocumentTitle?: string | null; DocumentNumber?: string | null;
    DocumentRevision?: string | null; ContentSHA256?: string | null;
    ReadByModel?: string | null; OCRConfidence?: number | null; OCRNote?: string | null;
    ReadByAModel?: boolean; Confirmed?: boolean; MayPromote?: boolean;
    PromotedRevisionUSID?: string | null; ChangeOrderUSID?: string | null;
};

type Queue = {
    Count?: number; Staged?: number; Reading?: number; Read?: number; Unreadable?: number;
    Corrected?: number; Promoted?: number; Discarded?: number;
    AwaitingConfirmation?: number; PagesRead?: number; Readings?: number;
    DuplicatesRefused?: number;
};

function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

function when(value?: string | null): string {
    if (!value) return "—";
    const at = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 16);
    return at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

function spell(value?: string | null): string {
    const text = String(value ?? "").replace(/_/g, " ").toLowerCase();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : "—";
}

/** The one-word state, and the colour that says whether somebody has to do something. */
function tone(row: Import_): string {
    if (row.Status === "PROMOTED") return "#1B5E20";
    if (row.Status === "DISCARDED") return Grey7;
    if (row.Status === "UNREADABLE") return "#B3261E";
    if (row.ReadByAModel && !row.Confirmed) return "#8A6100";
    return Grey8;
}

function Count({label, value, note}: {label: string; value?: number; note?: string}) {
    const body = <Box sx={{minWidth: 112}}>
        <Typography sx={{fontSize: 24, color: Grey17, lineHeight: 1.2}}>{value ?? 0}</Typography>
        <Typography sx={{fontSize: 12, color: Grey7}}>{label}</Typography>
    </Box>;
    return note ? <Tooltip title={note}><span>{body}</span></Tooltip> : body;
}

export default function DocumentImports() {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);
    const navigate = useNavigate();

    const [queue, setQueue] = useState<Queue | null>(null);
    const [rows, setRows] = useState<Import_[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.get("t1/v1/document_import_status"),
            api.get("t1/v1/document_imports", {params: {Limit: 200}}),
        ])
            .then(([counts, listed]: any[]) => {
                setQueue(counts?.data?.PlatformMessage ?? null);
                setRows(listOf<Import_>(
                    listed?.data?.PlatformMessage?.ListOfDocumentImport?.DocumentImport));
                setError(null);
            })
            .catch((problem: any) => {
                setQueue(null);
                setError(problem?.response?.data?.Cause
                         ?? "The import queue could not be read.");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading && !queue && !error) {
        return <Box sx={{display: "flex", gap: "10px", alignItems: "center", padding: "24px"}}>
            <CircularProgress size={18}/>
            <Typography sx={{fontSize: 13, color: Grey7}}>Reading the import queue…</Typography>
        </Box>;
    }

    if (error) {
        return <Box sx={{padding: "24px"}}>
            <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.6, maxWidth: 760}}>
                {error}
            </Typography>
        </Box>;
    }

    const waiting = queue?.AwaitingConfirmation ?? 0;

    return <Box sx={{padding: "24px", maxWidth: 1400}}>
        <Typography sx={{fontSize: 20, color: Grey17}}>Document imports</Typography>
        <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 820,
                         paddingTop: "6px"}}>
            Scans and PDFs on their way to becoming controlled documents. An import here has been
            read by a machine and corrected by nobody until somebody opens it.
        </Typography>

        <Box sx={{display: "flex", gap: "28px", flexWrap: "wrap", padding: "22px 0 6px"}}>
            <Count label="in the queue" value={queue?.Count}/>
            <Count label="not read yet" value={queue?.Staged}/>
            <Count label="being read" value={queue?.Reading}
                   note="An OCR job is in flight. Open the import to collect it."/>
            {/* THE THIRD ANSWER, and the one a read-versus-unread list would show as finished. */}
            <Count label="waiting for a person" value={waiting}
                   note="Read by a machine and confirmed by nobody. These cannot be promoted."/>
            <Count label="under control" value={queue?.Promoted}/>
            <Count label="pages read" value={queue?.PagesRead}
                   note="What OCR has been billed for, in the unit the service charges in."/>
            <Count label="duplicates refused" value={queue?.DuplicatesRefused}
                   note="Files that were byte-for-byte one already imported. A refusal leaves no
                         row, so this count is the only place it can be seen."/>
        </Box>

        {waiting > 0 && (
            <Typography sx={{fontSize: 12.5, color: "#8A6100", lineHeight: 1.6, maxWidth: 820,
                             paddingBottom: "10px"}}>
                {waiting === 1 ? "One import has" : `${waiting} imports have`} been read by a
                machine and checked by nobody. An OCR engine reports a misread line as confidently
                as a correct one, so these cannot become controlled documents until somebody has
                been through the pages.
            </Typography>
        )}

        {rows.length === 0 && (
            <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 820,
                             paddingTop: "14px"}}>
                Nothing has been imported yet. A scan arrives here once it has been uploaded and
                staged, and is fingerprinted on the way in so the same file cannot be imported
                twice.
            </Typography>
        )}

        {rows.length > 0 && (
            <Box sx={{paddingTop: "10px"}}>
                <Box sx={{display: "grid", gridTemplateColumns: "2fr 1fr 1.2fr 1.4fr 1fr",
                          gap: "12px", padding: "8px 10px", borderBottom: `1px solid ${Grey2}`}}>
                    {["What it says it is", "Origin", "Imported", "Read by", "State"]
                        .map(head => (
                            <Typography key={head}
                                        sx={{fontSize: 11.5, color: Grey7,
                                             textTransform: "uppercase", letterSpacing: "0.04em"}}>
                                {head}
                            </Typography>
                        ))}
                </Box>
                {rows.map(row => (
                    <Box key={row.USID}
                         onClick={() => navigate(`/document-imports/${row.USID}`)}
                         sx={{display: "grid",
                              gridTemplateColumns: "2fr 1fr 1.2fr 1.4fr 1fr", gap: "12px",
                              padding: "11px 10px", borderBottom: `1px solid ${Grey2}`,
                              cursor: "pointer", "&:hover": {backgroundColor: Grey5}}}>
                        <Box>
                            <Typography sx={{fontSize: 13, color: Grey17}}>
                                {row.DocumentTitle || "Untitled — nobody has read a title off it"}
                            </Typography>
                            {(row.DocumentNumber || row.DocumentRevision) && (
                                <Typography sx={{fontSize: 12, color: Grey7}}>
                                    {row.DocumentNumber}
                                    {row.DocumentRevision ? ` rev ${row.DocumentRevision}` : ""}
                                </Typography>
                            )}
                        </Box>
                        <Box>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.Origin}</Typography>
                            {row.OriginDetail && (
                                <Typography sx={{fontSize: 11.5, color: Grey7}}>
                                    {row.OriginDetail}
                                </Typography>
                            )}
                        </Box>
                        <Typography sx={{fontSize: 12.5, color: Grey8}}>
                            {when(row.ImportedDatetime)}
                        </Typography>
                        <Box>
                            {/* THE ENGINE IS NAMED, and whether anybody stood behind it. A screen
                                that showed the text without this would present a machine's guess
                                in the same typeface as a checked fact. */}
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>
                                {row.ReadByModel || "nothing yet"}
                            </Typography>
                            {row.ReadByAModel && !row.Confirmed && (
                                <Typography sx={{fontSize: 11.5, color: "#8A6100"}}>
                                    unconfirmed — a claim rather than a fact
                                </Typography>
                            )}
                            {row.Confirmed && (
                                <Typography sx={{fontSize: 11.5, color: "#1B5E20"}}>
                                    confirmed by a person
                                </Typography>
                            )}
                        </Box>
                        <Box>
                            <Chip label={spell(row.Status)} size="small"
                                  sx={{fontSize: 11, height: 22,
                                       color: tone(row), borderColor: tone(row)}}
                                  variant="outlined"/>
                        </Box>
                    </Box>
                ))}
            </Box>
        )}
    </Box>;
}
